// src/types/photoSharing.ts
export type LayoutType =
   | 'grid-square'
   | 'grid-instagram'
   | 'masonry-pinterest'
   | 'single-hero'
   | 'story-horizontal'
   | 'collage-puzzle'
   | 'asymmetrical-modern'
   | 'bordered-classic'
   | 'magazine-editorial'
   | 'mosaic-creative';

export interface LayoutOption {
   id: LayoutType;
   name: string;
   description: string;
   thumbnail: string;
   maxPhotos?: number;
   minPhotos?: number;
}

export interface SelectedPhoto {
   id: string;
   url: string;
   thumbnailUrl: string;
   title: string;
   description?: string;
   takenAt?: Date;
   selected: boolean;
}

export interface PhotoSharingRecipient {
   id: string;
   email: string;
   displayName?: string;
   photoURL?: string;
   role?: 'owner' | 'editor' | 'viewer' | 'participant';
   status?: 'active' | 'pending' | 'declined';
   isParticipant: boolean; // true for participants, false for non-participants
}

export interface PhotoSharingData {
   selectedPhotos: SelectedPhoto[];
   layout: LayoutType;
   recipients: {
      participants: PhotoSharingRecipient[];
      nonParticipants: PhotoSharingRecipient[];
   };
   message?: string;
   subject?: string;
}

export interface PhotoSharingRequest {
   entityType: 'trip' | 'event' | 'album';
   entityId: string;
   entityTitle: string;
   sharingData: PhotoSharingData;
   createdBy: string;
   createdAt: Date;
}

export interface PhotoSharingResponse {
   success: boolean;
   message: string;
   sharingId?: string;
   emailsSent?: {
      participants: string[];
      nonParticipants: string[];
   };
   errors?: string[];
}

// Layout configurations based on popular online photo grid layouts
export const LAYOUT_OPTIONS: LayoutOption[] = [
   {
      id: 'grid-square',
      name: 'Square Grid',
      description: 'Clean uniform square grid - perfect for any number of photos',
      thumbnail: '/styles/grid-square-preview.jpg',
      maxPhotos: 9,
      minPhotos: 1,
   },
   {
      id: 'grid-instagram',
      name: 'Instagram Grid',
      description: 'Popular 3x3 Instagram-style layout',
      thumbnail: '/styles/grid-instagram-preview.jpg',
      maxPhotos: 9,
      minPhotos: 1,
   },
   {
      id: 'masonry-pinterest',
      name: 'Pinterest Masonry',
      description: 'Dynamic Pinterest-style staggered layout',
      thumbnail: '/styles/masonry-pinterest-preview.jpg',
      maxPhotos: 8,
      minPhotos: 3,
   },
   {
      id: 'single-hero',
      name: 'Hero Spotlight',
      description: 'Single large featured photo with impact',
      thumbnail: '/styles/single-hero-preview.jpg',
      maxPhotos: 1,
      minPhotos: 1,
   },
   {
      id: 'story-horizontal',
      name: 'Story Strip',
      description: 'Horizontal Instagram/Facebook story-style layout',
      thumbnail: '/styles/story-horizontal-preview.jpg',
      maxPhotos: 5,
      minPhotos: 2,
   },
   {
      id: 'collage-puzzle',
      name: 'Puzzle Collage',
      description: 'Interlocking puzzle-piece photo arrangement',
      thumbnail: '/styles/collage-puzzle-preview.jpg',
      maxPhotos: 6,
      minPhotos: 3,
   },
   {
      id: 'asymmetrical-modern',
      name: 'Modern Asymmetrical',
      description: 'Trendy asymmetrical layout with mixed sizes',
      thumbnail: '/styles/asymmetrical-modern-preview.jpg',
      maxPhotos: 7,
      minPhotos: 3,
   },
   {
      id: 'bordered-classic',
      name: 'Classic Bordered',
      description: 'Traditional bordered photo grid layout',
      thumbnail: '/styles/bordered-classic-preview.jpg',
      maxPhotos: 6,
      minPhotos: 2,
   },
   {
      id: 'magazine-editorial',
      name: 'Magazine Editorial',
      description: 'Professional magazine-style photo spread',
      thumbnail: '/styles/magazine-editorial-preview.jpg',
      maxPhotos: 8,
      minPhotos: 3,
   },
   {
      id: 'mosaic-creative',
      name: 'Creative Mosaic',
      description: 'Artistic mosaic with varied photo sizes',
      thumbnail: '/styles/mosaic-creative-preview.jpg',
      maxPhotos: 10,
      minPhotos: 4,
   },
];


